/**
 * ToastContainer - Displays toast notifications
 */

import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, type Toast, type ToastType } from '@stores/toast.store';

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={20} className="text-ybh-success" />,
  error: <XCircle size={20} className="text-ybh-error" />,
  warning: <AlertTriangle size={20} className="text-ybh-warning" />,
  info: <Info size={20} className="text-ybh-blue-light" />,
};

const colors: Record<ToastType, string> = {
  success: 'border-ybh-success/40 bg-ybh-success/10',
  error: 'border-ybh-error/40 bg-ybh-error/10',
  warning: 'border-ybh-warning/40 bg-ybh-warning/10',
  info: 'border-ybh-blue/40 bg-ybh-blue/10',
};

function ToastItem({ toast }: { toast: Toast }): JSX.Element {
  const { removeToast } = useToastStore();

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border backdrop-blur-sm
        animate-slide-up shadow-lg
        ${colors[toast.type]}
      `}
    >
      <div className="flex-shrink-0 mt-0.5">{icons[toast.type]}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-ybh-text-primary">{toast.title}</p>
        {toast.message && (
          <p className="text-sm text-ybh-text-secondary mt-0.5">{toast.message}</p>
        )}
      </div>
      <button
        onClick={() => removeToast(toast.id)}
        className="flex-shrink-0 p-1 rounded hover:bg-ybh-bg-tertiary transition-colors"
      >
        <X size={16} className="text-ybh-text-muted" />
      </button>
    </div>
  );
}

export function ToastContainer(): JSX.Element {
  const { toasts } = useToastStore();

  if (toasts.length === 0) return <></>;

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex flex-col gap-2 max-w-sm w-full">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
